import { Section } from "@/app/components/brand-scaffold";
import { protocolBenefits } from "@/design-system/registry/homepage";
import { ResponderConsoleMockup } from "./homepage-responder-console-mockup";

/**
 * S5 · Protocol Benefits — 协议替你做的 5 件事
 *
 * Audit cleanup 2026-04-22:
 *   Old layout (left 5-stack + right small mockup) created severe visual
 *   imbalance: a tall mass of color cards on the left, tiny mockup floating
 *   on the right with 280px of dead space below it.
 *
 * New layout — vertical rhythm, mockup as the FIRST focal block:
 *   1. Mockup band  (full-width, 2-column at lg+: console mockup | rationale)
 *   2. 5 benefit cards in a 2/3 col grid (md → 2 cols, lg → 3 cols)
 *      The 5th card spans the empty cell on the right with a closing line.
 */
export function HomepageProtocolBenefits() {
  return (
    <Section
      id="benefits"
      eyebrow="WHAT THE PROTOCOL DOES FOR YOU"
      title="你只写你独有的那部分，剩下都在协议里"
      description="协议层把 SaaS 行业过去要单独开发的 5 件事一次性收编。下方界面是你上架 Hotline 之后，每天打开的「管理后台」。"
    >
      {/* Block 1 — Mockup band as the SECTION OPENER (visual anchor). */}
      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr] lg:items-stretch">
        <ResponderConsoleMockup />
        <div className="border-4 border-black bg-black text-white p-6 lg:p-7 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] flex flex-col justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-widest text-[#A3E635]">
              你日常打开的画面
            </p>
            <h3 className="mt-3 text-xl sm:text-2xl font-black text-white leading-tight">
              数字分身的管理后台
            </h3>
            <p className="mt-4 text-sm font-bold text-zinc-300 leading-relaxed">
              <span className="font-black text-white">本月调用数 / 本月结算 / 评分趋势 / 调用记录 / 版本管理</span>
              ——协议层都做完了，你只需要每天看一眼，不用再为运营花一行代码。
            </p>
          </div>
          <div className="mt-6 pt-5 border-t-2 border-dashed border-white/30 flex flex-wrap gap-2">
            <span className="border-2 border-[#A3E635] bg-[#A3E635]/15 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-[#A3E635]">
              5 件事
            </span>
            <span className="border-2 border-white/40 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white/80">
              一次收编
            </span>
            <span className="border-2 border-white/40 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white/80">
              零额外代码
            </span>
          </div>
        </div>
      </div>

      {/* Block 2 — 5 benefit cards in a balanced 2/3-column grid.
          mobile: 1 col · md: 2 cols · lg+: 3 cols (5th card spans 2 cols on lg+
          to gracefully fill the empty 6th cell). */}
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {protocolBenefits.map((card, i) => {
          const isLast = i === protocolBenefits.length - 1;
          return (
            <article
              key={card.num}
              className={`border-4 border-black ${card.bg} ${card.hoverBg} p-5 sm:p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] flex flex-col gap-3 ${isLast ? "md:col-span-2 lg:col-span-2" : ""}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="text-3xl sm:text-4xl font-black text-black/70 leading-none">
                  {card.num}
                </span>
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-700 leading-tight">
                  {card.engineering}
                </p>
              </div>
              <h3 className="text-lg sm:text-xl font-black text-black leading-tight">
                {card.business}
              </h3>
              <p className="text-sm font-bold text-zinc-800 leading-relaxed">
                {card.detail}
              </p>
            </article>
          );
        })}
      </div>
    </Section>
  );
}
